// 表のセル編集フック。
//
// 既存の本文編集フック（use-object-text-editor）へは相乗りしない。あちらは Markdown の WYSIWYG 編集
// （beforeinput の横取り・正準ブロックの再構築・部分書式の rebase・リンク化）を抱えており、
// セル編集はそのどれも要らない。セルは 1 行のプレーンテキストなので contentEditable ではなく
// <input type="text"> で足り、IME・全選択・キャレットの扱いをブラウザへ任せられる。
//
// このフックが唯一の所有者として扱うもの:
//  - 草稿（draft）と選択範囲
//  - 入力履歴（Ctrl+Z / Ctrl+Y。input.value の直接代入とブラウザ履歴を混在させない）
//  - IME の変換状態
//  - debounce による送信と、終了時の flush / cancel の振り分け
//  - インライン入力と補助入力の切り替え（入力 DOM を同時に 2 個有効にしない）

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  cellInlineStylesAt,
  fitTableCellText,
  tableStructureRevisionOf,
  TABLE_MAX_ROWS,
  type TableCanvasObject,
  type TextInlineStyleRange,
} from "@refinement-canvas/domain";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { useCanvasStore } from "../store/canvas-store";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";
import { useShowToast, type ShowToast } from "../notice/use-show-toast";
import type { CanvasOpDispatcher } from "../use-canvas-op-dispatcher";
import {
  useTableCellEditorStore,
  TABLE_CELL_PANEL_ATTRIBUTE,
  type TableCellEditorHandle,
  type TableCellInputHandlers,
  type TableCellMoveDirection,
  type TableCellSelection,
  type TableCellAnchorRect,
} from "./table-cell-editor-store";

/** 入力から送信までの debounce（既存の本文編集と同じ間隔）。 */
const TABLE_CELL_SEND_DEBOUNCE_MS = 50;

/** 1 回のセル滞在で保持する入力履歴の最大ステップ数。 */
const TABLE_CELL_HISTORY_LIMIT = 512;

/**
 * インライン入力で操作できると判断する最小の画面上サイズ（CSS px）。
 *
 * 幅は、表図形の描画が保証する最小列幅（`TABLE_MIN_COLUMN_EM` = 4em、既定フォント 16px で 64px）に
 * そろえる。高さは最小行高（`TABLE_ROW_MIN_HEIGHT_EM` = 2em = 32px）の半分を採り、
 * ズームで縮小されてもキャレットと数文字が見える範囲を残す。
 *
 * **既定の表（360 × 96 px の 3 行 3 列）のセルは 119.33 × 32 px になる。** 閾値を
 * 120 × 32 px にすると、置いたばかりの表がすべて補助入力へ倒れ、表の中で直接入力できなくなる。
 * 補助入力は極小・低倍率・画面外の表のための代替経路であり、通常の表の既定経路ではない。
 */
const TABLE_CELL_INLINE_MIN_WIDTH_PX = 64;
const TABLE_CELL_INLINE_MIN_HEIGHT_PX = 16;

/** 1 回のセル滞在ぶんの入力履歴 1 ステップ。 */
interface CellHistoryStep {
  readonly value: string;
  readonly selectionStart: number;
  readonly selectionEnd: number;
}

/**
 * 1 回のセル滞在で積んだ操作を、どちらの履歴で戻すか。
 *
 * セル内の Ctrl+Z / Ctrl+Y は 2 系統を戻す必要がある。
 *
 * - `text`: 未確定の本文入力。このフックが持つスナップショットスタックが正本。
 * - `canvas`: キャンバス履歴へ積み終えた項目（書式適用と、書式適用の直前に閉じた入力グループ）。
 *
 * `canvas` の取り消しは dispatcher の undo / redo（LIFO）へ委ねるため、**キャンバス履歴の並びと
 * この列の `canvas` の並びが必ず 1 対 1 で対応していなければならない。** そのため、キャンバス履歴へ
 * 項目を積んだ時点で、対応する `text` は 1 件の `canvas` へ畳む（{@link useTableCellEditor} の
 * `prepareStyleApply`）。
 */
type CellHistoryKind = "text" | "canvas";

/** セル編集を開始する共通の入口（ダブルクリック・F2・「セルを編集」で共有する）。 */
export function beginTableCellEdit(objectId: string, row = 0, column = 0): boolean {
  const ui = useCanvasStore.getState();
  if (ui.readOnly) return false;
  const object = useCanvasDocStore.getState().objects[objectId];
  if (!object || object.type !== "table" || object.locked) return false;
  const targetRow = object.cells[row] !== undefined ? row : 0;
  const targetColumn = object.cells[targetRow]?.[column] !== undefined ? column : 0;
  if (object.cells[targetRow]?.[targetColumn] === undefined) return false;
  // 表以外の編集・範囲選択とは同時に成立させない。
  ui.setActiveTextRangeSelection(null);
  ui.setAutoEditObjectId(null);
  if (ui.selectedObjectIds.length !== 1 || ui.selectedObjectIds[0] !== objectId) {
    ui.setSelectedObjectIds([objectId]);
  }
  ui.setTableCellEditing({ objectId, row: targetRow, column: targetColumn });
  return true;
}

/** セル編集を終了する（草稿の確定は編集器側の effect が担う）。 */
export function endTableCellEdit(): void {
  useCanvasStore.getState().setTableCellEditing(null);
}

export interface TableCellEditorApi {
  /** この表で編集中のセル。別の表を編集中・非編集なら null。 */
  readonly editingCell: { readonly row: number; readonly column: number } | null;
  /** 編集中セルの草稿。 */
  readonly draft: string;
  /** 補助入力へ切り替えているか（インライン入力欄は描画しない）。 */
  readonly auxiliary: boolean;
  /** 表全体のラッパー（編集中だけスクロールする要素）の ref。 */
  readonly wrapperRef: (element: HTMLDivElement | null) => void;
  /** インライン入力欄の ref。 */
  readonly inputRef: (element: HTMLInputElement | null) => void;
  /** セルのダブルクリックから編集を開始する。 */
  readonly beginEditAt: (row: number, column: number) => void;
  /** インライン入力欄へ渡すイベントハンドラ群（補助入力欄も同じものを使う）。 */
  readonly inputHandlers: TableCellInputHandlers;
}

export function useTableCellEditor(object: TableCanvasObject): TableCellEditorApi {
  const dispatcher = useCanvasOpDispatcherContext();
  const showToast = useShowToast();
  const editing = useCanvasStore((state) => state.tableCellEditing);
  const selectedObjectIds = useCanvasStore((state) => state.selectedObjectIds);
  const readOnly = useCanvasStore((state) => state.readOnly);
  // 表示位置（パン・ズーム）。同じセルでも画面上の矩形が変わるため、実測のやり直しの契機にする。
  const view = useCanvasStore((state) => state.view);
  const draft = useTableCellEditorStore((state) => state.draft);
  const auxiliary = useTableCellEditorStore((state) => state.auxiliary);
  const setDraftValue = useTableCellEditorStore((state) => state.setDraft);
  const setAuxiliary = useTableCellEditorStore((state) => state.setAuxiliary);
  const setHandle = useTableCellEditorStore((state) => state.setHandle);
  const setSelectionState = useTableCellEditorStore((state) => state.setSelection);
  const setAnchorRect = useTableCellEditorStore((state) => state.setAnchorRect);
  const resetEditorStore = useTableCellEditorStore((state) => state.reset);

  const active = editing !== null && editing.objectId === object.id;
  const [inputElement, setInputElement] = useState<HTMLInputElement | null>(null);
  const wrapperElementRef = useRef<HTMLDivElement | null>(null);

  // 送信・履歴・IME の状態は再描画をまたいで保つため ref に持つ（描画には使わない）。
  const draftRef = useRef("");
  const baseTextRef = useRef("");
  // 編集を開始した時点のそのセルの部分書式。Undo は確定後の run から逆算できない
  // （書式が付いていた文字ごと消すと run が空になり、そこから書式を作り直せない）ため、
  // 開始時点の値をこの ref で持ち回り、履歴の逆 op へそのまま載せる。
  const baseInlineStylesRef = useRef<readonly TextInlineStyleRange[] | undefined>(undefined);
  const sendTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const composingRef = useRef(false);
  const compositionJustEndedRef = useRef(false);
  const historyRef = useRef<{ past: CellHistoryStep[]; future: CellHistoryStep[] }>({
    past: [],
    future: [],
  });
  // 本文スナップショットと書式適用を、積んだ順に戻すための種別列（上の historyRef と対で動く）。
  const historyKindsRef = useRef<{ past: CellHistoryKind[]; future: CellHistoryKind[] }>({
    past: [],
    future: [],
  });
  const pendingSelectionRef = useRef<{ start: number; end: number } | null>(null);
  // 編集位置（row / column）を決めた時点の構造 revision。行・列が増減すると同じ添字が別のセルを
  // 指すため、送信時にはこの値を使い、現在値で組み直さない。
  const cellRef = useRef<{
    objectId: string;
    row: number;
    column: number;
    structureRevision: number;
  } | null>(null);

  const objectRef = useRef(object);
  objectRef.current = object;
  const dispatcherRef = useRef(dispatcher);
  dispatcherRef.current = dispatcher;

  const clearSendTimer = useCallback((): void => {
    if (sendTimerRef.current === null) return;
    clearTimeout(sendTimerRef.current);
    sendTimerRef.current = null;
  }, []);

  /**
   * 選択範囲を、送信・履歴で使う ref と共有ストアの両方へ記録する。
   * 共有ストア側は書式パネルが「セル用か範囲用か」「どこへ当てるか」を決める正本になるため、
   * 片方だけを更新するとパネルの表示と実際の適用範囲がずれる。
   */
  const rememberSelection = useCallback(
    (selection: TableCellSelection | null): void => {
      pendingSelectionRef.current = selection === null ? null : { ...selection };
      setSelectionState(selection);
    },
    [setSelectionState]
  );

  /** 現在の草稿を即時に送る（debounce を待たない）。 */
  const flushSend = useCallback((): void => {
    clearSendTimer();
    const cell = cellRef.current;
    if (!cell) return;
    dispatcherRef.current.updateTableCell(
      cell.objectId,
      cell.row,
      cell.column,
      draftRef.current,
      cell.structureRevision
    );
  }, [clearSendTimer]);

  const scheduleSend = useCallback((): void => {
    clearSendTimer();
    sendTimerRef.current = setTimeout(() => {
      sendTimerRef.current = null;
      const cell = cellRef.current;
      if (!cell) return;
      dispatcherRef.current.updateTableCell(
        cell.objectId,
        cell.row,
        cell.column,
        draftRef.current,
        cell.structureRevision
      );
    }, TABLE_CELL_SEND_DEBOUNCE_MS);
  }, [clearSendTimer]);

  /**
   * 1 回のセル滞在ぶんの差分を、キャンバス履歴へ 1 グループとして確定する。
   * 戻り値は「履歴項目を積んだか」（差分ゼロ・送信拒否では積まれない）。
   */
  const commitCellHistory = useCallback((): boolean => {
    const cell = cellRef.current;
    if (!cell) return false;
    const recorded = dispatcherRef.current.recordTableCellHistory(
      cell.objectId,
      cell.row,
      cell.column,
      baseTextRef.current,
      draftRef.current,
      baseInlineStylesRef.current
    );
    baseTextRef.current = draftRef.current;
    // 基準（本文・run）を「いま確定した内容」へ進める。同じセルに留まったまま 2 回目の確定が
    // 起きる経路（行の上限で移動できず編集が続く場合など）で、2 回目の逆 op が編集開始時の
    // 古い run を載せないようにする。表は楽観適用で既に更新済みなので、描画をまたがない
    // この時点では props（objectRef）ではなくストアの最新値から読む。
    const latest = useCanvasDocStore.getState().objects[cell.objectId];
    baseInlineStylesRef.current =
      latest && latest.type === "table"
        ? cellInlineStylesAt(latest, cell.row, cell.column)
        : undefined;
    return recorded;
  }, []);

  /** 草稿を差し替え、入力欄の表示・キャレット・送信予約をそろえる。 */
  const applyDraft = useCallback(
    (
      value: string,
      selection: { start: number; end: number } | undefined,
      options: { readonly schedule: boolean }
    ): void => {
      const cell = cellRef.current;
      if (!cell) return;
      const table = objectRef.current;
      const fitted = fitTableCellText({
        cells: table.cells,
        row: cell.row,
        column: cell.column,
        text: value,
      });
      if (!fitted) return;
      if (fitted.clamped) {
        showToast(
          "warning",
          "表に入れられる文字数の上限を超えたため、入りきらない分を切り詰めました。"
        );
      }
      draftRef.current = fitted.text;
      setDraftValue(fitted.text);
      const caret = Math.min(selection?.start ?? fitted.text.length, fitted.text.length);
      const caretEnd = Math.min(selection?.end ?? caret, fitted.text.length);
      rememberSelection({ start: caret, end: caretEnd });
      if (inputElement) {
        if (inputElement.value !== fitted.text) inputElement.value = fitted.text;
        inputElement.setSelectionRange(caret, caretEnd);
      }
      if (options.schedule) scheduleSend();
      dispatcherRef.current.previewTableCellCursor(
        cell.objectId,
        { row: cell.row, column: cell.column },
        caret
      );
    },
    [inputElement, rememberSelection, scheduleSend, setDraftValue, showToast]
  );

  /** 変更前の値と選択範囲を履歴へ積む（Redo 側は破棄する）。 */
  const pushHistory = useCallback((): void => {
    const selection = pendingSelectionRef.current ?? {
      start: draftRef.current.length,
      end: draftRef.current.length,
    };
    const history = historyRef.current;
    const kinds = historyKindsRef.current;
    history.past.push({
      value: draftRef.current,
      selectionStart: selection.start,
      selectionEnd: selection.end,
    });
    kinds.past.push("text");
    if (history.past.length > TABLE_CELL_HISTORY_LIMIT) {
      history.past.shift();
      // 捨てたスナップショットに対応する種別も 1 件だけ落とし、2 つの記録の対応を保つ
      // （ずれると Ctrl+Z が本文と書式のどちらを戻すかを取り違える）。
      const oldest = kinds.past.indexOf("text");
      if (oldest >= 0) kinds.past.splice(oldest, 1);
    }
    history.future.length = 0;
    kinds.future.length = 0;
  }, []);

  /**
   * 書式パネルからの適用が成立したことを記録する。
   * 書式そのものの取り消しはキャンバス履歴が持つため、ここでは順序だけを覚える。
   */
  const notifyStyleApplied = useCallback((): void => {
    const kinds = historyKindsRef.current;
    kinds.past.push("canvas");
    kinds.future.length = 0;
    // 新しい操作を行ったので、本文側の Redo も無効にする。
    historyRef.current.future.length = 0;
  }, []);

  /**
   * 書式適用の直前に、未送信の本文を送り、そこまでの入力を履歴の 1 グループとして閉じる。
   *
   * 閉じないと、キャンバス履歴が「書式 → 入力グループ」の順になり、編集を抜けた後の Ctrl+Z が
   * 「入力を取り消す → 書式を取り消して本文が戻る」という前後した並びになる。
   * 閉じた入力は**セル内のスタックから外し、種別列では 1 件の `canvas` へ畳む。** こうしないと、
   * 書式を挟んだ入力をセル内で取り消したときに、種別列の `canvas` とキャンバス履歴の並びがずれ、
   * 別の項目を取り消してしまう。
   */
  const prepareStyleApply = useCallback((): void => {
    flushSend();
    const recorded = commitCellHistory();
    const kinds = historyKindsRef.current;
    while (kinds.past[kinds.past.length - 1] === "text") kinds.past.pop();
    if (recorded) kinds.past.push("canvas");
    kinds.future.length = 0;
    historyRef.current = { past: [], future: [] };
  }, [commitCellHistory, flushSend]);

  const applyHistoryStep = useCallback(
    (direction: "undo" | "redo"): boolean => {
      const history = historyRef.current;
      const from = direction === "undo" ? history.past : history.future;
      const to = direction === "undo" ? history.future : history.past;
      const step = from.pop();
      if (!step) return false;
      const selection = pendingSelectionRef.current ?? {
        start: draftRef.current.length,
        end: draftRef.current.length,
      };
      to.push({
        value: draftRef.current,
        selectionStart: selection.start,
        selectionEnd: selection.end,
      });
      applyDraft(step.value, { start: step.selectionStart, end: step.selectionEnd }, {
        schedule: true,
      });
      return true;
    },
    [applyDraft]
  );

  /**
   * セル内の Ctrl+Z / Ctrl+Y を、積んだ順どおりに本文・キャンバス履歴のどちらかへ振り分ける。
   * キャンバス履歴が正本の項目（書式適用と、閉じた入力グループ）は dispatcher の undo / redo を呼ぶ。
   */
  const stepHistory = useCallback(
    (direction: "undo" | "redo"): void => {
      const kinds = historyKindsRef.current;
      const from = direction === "undo" ? kinds.past : kinds.future;
      const to = direction === "undo" ? kinds.future : kinds.past;
      const kind = from.pop();
      if (kind === undefined) return;
      if (kind === "text") {
        // 本文側の記録が尽きていた場合は種別も戻し、2 つの記録がずれたままにしない。
        if (!applyHistoryStep(direction)) {
          from.push(kind);
          return;
        }
      } else if (direction === "undo") {
        dispatcherRef.current.undo();
      } else {
        dispatcherRef.current.redo();
      }
      to.push(kind);
    },
    [applyHistoryStep]
  );

  /**
   * 方向に応じた移動先を求め、必要なら行を 1 行追加する。
   * 移動の前に編集中セルの本文を確定して送る（待つと、移動直後に前のセルの op が届いて
   * 上書きが遅れる）。行の自動追加より先に確定させることで、追加前の構造に対する
   * セル更新として送れる。
   */
  const moveCell = useCallback(
    (direction: TableCellMoveDirection): void => {
      const cell = cellRef.current;
      if (!cell) return;
      const table = objectRef.current;
      const rowCount = table.cells.length;
      const columnCount = table.cells[0]?.length ?? 0;
      if (rowCount === 0 || columnCount === 0) return;
      const target = resolveMoveTarget(direction, cell, rowCount, columnCount);
      if (target === null) return;
      flushSend();
      commitCellHistory();
      if (
        target.appendRow &&
        !appendRowForNavigation(table, showToast, dispatcherRef.current)
      ) {
        return;
      }
      useCanvasStore
        .getState()
        .setTableCellEditing({ objectId: cell.objectId, row: target.row, column: target.column });
    },
    [commitCellHistory, flushSend, showToast]
  );

  const endEdit = useCallback((): void => {
    flushSend();
    commitCellHistory();
    endTableCellEdit();
  }, [commitCellHistory, flushSend]);

  /** 未送信の入力を捨てて編集を終える（対象が消えた・ロックされた・形が変わった場合）。 */
  const cancelEdit = useCallback(
    (notice?: string): void => {
      clearSendTimer();
      // debounce 待ちだけでなく、送信キューで順番待ちの op も破棄する。先行する op の応答後に
      // 送り出されると、捨てたはずの入力がサーバへ届く（構造 revision 不一致で拒否されるだけの
      // 往復になり、楽観適用も拒否が返るまで「保存済み」の見た目で残る）。
      const cell = cellRef.current;
      if (cell) dispatcherRef.current.discardTableOps(cell.objectId);
      if (notice !== undefined) showToast("warning", notice);
      endTableCellEdit();
    },
    [clearSendTimer, showToast]
  );

  // 編集対象セルが変わるたびに、草稿・履歴・基準値を作り直す。
  const editingRow = active && editing ? editing.row : -1;
  const editingColumn = active && editing ? editing.column : -1;
  useEffect(() => {
    if (!active || editingRow < 0) {
      // この表が編集を終えた場合だけ、パネル用の状態も畳む。cellRef はフックごとに持つため、
      // 別の表を編集中に、編集していない表のフックが共有ストアを消してしまうことはない。
      if (cellRef.current !== null) {
        rememberSelection(null);
        setAnchorRect(null);
      }
      cellRef.current = null;
      return;
    }
    const value = objectRef.current.cells[editingRow]?.[editingColumn] ?? "";
    cellRef.current = {
      objectId: object.id,
      row: editingRow,
      column: editingColumn,
      structureRevision: tableStructureRevisionOf(objectRef.current.structureRevision),
    };
    draftRef.current = value;
    baseTextRef.current = value;
    baseInlineStylesRef.current = cellInlineStylesAt(objectRef.current, editingRow, editingColumn);
    historyRef.current = { past: [], future: [] };
    historyKindsRef.current = { past: [], future: [] };
    rememberSelection({ start: 0, end: value.length });
    setDraftValue(value);
    // 切り替え直後はインライン入力を既定とし、実測できしだい補助入力へ切り替える。
    setAuxiliary(false);
  }, [
    active,
    editingRow,
    editingColumn,
    object.id,
    rememberSelection,
    setAnchorRect,
    setAuxiliary,
    setDraftValue,
  ]);

  // 入力欄が現れたらフォーカスと全選択を行い、表内スクロールで見える位置へ寄せる。
  useEffect(() => {
    if (!active || !inputElement) return;
    inputElement.value = draftRef.current;
    inputElement.focus({ preventScroll: true });
    const selection = pendingSelectionRef.current ?? {
      start: 0,
      end: draftRef.current.length,
    };
    inputElement.setSelectionRange(selection.start, selection.end);
    scrollEditingCellIntoView(wrapperElementRef.current, inputElement);
  }, [active, inputElement, editingRow, editingColumn]);

  // インライン入力で操作できる大きさ・位置かを実測し、足りなければ補助入力へ切り替える。
  //
  // 実測の対象は、インライン入力ではなく**編集中のセル**にする。入力欄を対象にすると、
  // 補助入力へ切り替えた瞬間にインライン入力が DOM から消え、代わりに測った表全体が
  // 「十分に大きい」と判定されてインライン入力へ戻り、以後その 2 つを往復し続ける
  // （セルは小さいが表そのものは大きい表で実際に起きる）。セルはどちらの表示方式でも
  // 存在し、入力できる領域そのものを表すため、判定が安定する。
  //
  // あわせて、そのセルの画面矩形を書式パネルの配置基準として記録する。測る対象・契機が
  // 補助入力の判定とまったく同じため、監視を二重に持たずにここへ相乗りさせる。
  //
  // 表示位置（`view`）も依存に含める。パン・ズームでは window の resize が起きないまま、同じセルの
  // 画面矩形だけが変わるためである。書式パネル自身も Ctrl / Meta + ホイールでズームを起こすので、
  // 購読しないとパネルを操作した直後に、パネルが編集中のセルから離れた位置へ取り残される。
  // 補助入力へ倒すかの判定も同じ理由で測り直す（縮小でセルが極小・画面外になった場合に追従する）。
  useEffect(() => {
    if (!active) return undefined;
    const evaluate = (): void => {
      const wrapper = wrapperElementRef.current;
      const cell =
        wrapper?.querySelector<HTMLElement>(
          `[data-table-cell="${editingRow}-${editingColumn}"]`
        ) ?? null;
      setAuxiliary(!canUseInlineInput(cell ?? wrapper));
      setAnchorRect(readCellAnchorRect(cell));
    };
    evaluate();
    if (typeof window === "undefined") return undefined;
    window.addEventListener("resize", evaluate);
    return () => window.removeEventListener("resize", evaluate);
  }, [active, inputElement, editingRow, editingColumn, setAnchorRect, setAuxiliary, view]);

  // 対象が消えた・ロックされた・参照専用になった・セルが範囲外になった場合の後始末。
  useEffect(() => {
    if (!active) return;
    const current = useCanvasDocStore.getState().objects[object.id];
    if (!current || current.type !== "table") {
      cancelEdit();
      return;
    }
    if (current.locked === true || readOnly) {
      cancelEdit();
      return;
    }
    if (current.cells[editingRow]?.[editingColumn] === undefined) {
      cancelEdit("編集中のセルが削除されたため、未保存の入力は破棄しました。");
      return;
    }
    // 行・列が増減すると、同じ添字が別のセルを指す（増減後も範囲内のままなので、上の範囲検査では
    // 検知できない）。編集を開始した形と違う表になった時点で、草稿の宛先を確認できないため
    // 送信せずに編集を終える（最新の表で入力し直してもらう）。
    const cell = cellRef.current;
    if (cell && tableStructureRevisionOf(current.structureRevision) !== cell.structureRevision) {
      cancelEdit("表の行または列が変更されたため、未保存の入力は破棄しました。");
    }
  }, [active, cancelEdit, editingColumn, editingRow, object, readOnly]);

  // 選択が変わったら編集を終える（未送信入力は確定する）。
  useEffect(() => {
    if (!active) return;
    if (selectedObjectIds.length === 1 && selectedObjectIds[0] === object.id) return;
    endEdit();
  }, [active, endEdit, object.id, selectedObjectIds]);

  const rowCount = object.cells.length;
  const columnCount = object.cells[0]?.length ?? 0;
  const structureRevision = tableStructureRevisionOf(object.structureRevision);

  // アンマウント時にタイマーを解放する（別 canvas・破棄済みの表へ送らない）。
  // 共有ストアは、この表が編集中だった場合だけ初期化する（別の表の編集を巻き込まない）。
  // 表そのものが消えた場合（削除・キャンバス切替）は、この経路でしか編集状態を畳めない
  // （対象を監視する effect は、アンマウント後には動かない）。未送信の入力は対象が無いため捨てる。
  useEffect(
    () => () => {
      clearSendTimer();
      if (cellRef.current?.objectId !== object.id) return;
      // 送信キューで順番待ちの op も破棄する。表が消えた後に先行 op の応答が返ると、
      // 対象を失った入力がそのまま送り出されるため（監視 effect はアンマウント後に動かず、
      // この経路でしか片付けられない）。
      dispatcherRef.current.discardTableOps(object.id);
      resetEditorStore();
      endTableCellEdit();
    },
    [clearSendTimer, object.id, resetEditorStore]
  );

  const beginEditAt = useCallback(
    (row: number, column: number): void => {
      beginTableCellEdit(object.id, row, column);
    },
    [object.id]
  );

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value;
      const selectionStart = event.target.selectionStart ?? value.length;
      // IME 変換中は op を送らない。<input> は変換中にも input イベントを出すため、
      // debounce だけでは未確定の文字列が送られてしまう。
      if (composingRef.current) {
        draftRef.current = value;
        setDraftValue(value);
        pendingSelectionRef.current = { start: selectionStart, end: selectionStart };
        return;
      }
      pushHistory();
      applyDraft(value, { start: selectionStart, end: selectionStart }, { schedule: true });
    },
    [applyDraft, pushHistory, setDraftValue]
  );

  const onCompositionStart = useCallback((): void => {
    composingRef.current = true;
    clearSendTimer();
    pushHistory();
  }, [clearSendTimer, pushHistory]);

  const onCompositionEnd = useCallback(
    (event: React.CompositionEvent<HTMLInputElement>): void => {
      composingRef.current = false;
      // 変換確定の直後に来る Enter は「確定の余韻」なので、次の keydown で 1 回だけ読み捨てる。
      compositionJustEndedRef.current = true;
      const input = event.currentTarget;
      const selectionStart = input.selectionStart ?? input.value.length;
      applyDraft(input.value, { start: selectionStart, end: selectionStart }, { schedule: false });
      flushSend();
    },
    [applyDraft, flushSend]
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      // IME 変換中はセル移動を起こさない（変換確定の Enter を食わないため）。
      const composing =
        event.nativeEvent.isComposing || event.keyCode === 229 || composingRef.current;
      const justComposed = compositionJustEndedRef.current;
      // 「変換確定の余韻」として読み捨てるのは、compositionend の次に来る keydown 1 回だけ。
      // Enter のときだけ消費すると、確定のあとに通常入力を続けてから押した**実際の Enter**まで
      // 横取りしてしまう（Windows Chrome では確定の keydown が compositionend より先に来るため、
      // フラグは実 Enter まで残り続ける）。
      if (!composing) compositionJustEndedRef.current = false;
      if (composing) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        endEdit();
        return;
      }
      if (event.key === "Tab") {
        event.preventDefault();
        moveCell(event.shiftKey ? "previous" : "next");
        return;
      }
      if (event.key === "Enter") {
        if (justComposed) return;
        event.preventDefault();
        moveCell(event.shiftKey ? "up" : "down");
        return;
      }
      const key = event.key.toLowerCase();
      if ((event.ctrlKey || event.metaKey) && !event.altKey && (key === "z" || key === "y")) {
        // ブラウザ既定の入力履歴とキャンバスの Undo を止め、セル内の履歴だけを動かす。
        event.preventDefault();
        event.stopPropagation();
        const redo = key === "y" || (key === "z" && event.shiftKey);
        stepHistory(redo ? "redo" : "undo");
      }
    },
    [endEdit, moveCell, stepHistory]
  );

  const onBeforeInput = useCallback(
    (event: React.FormEvent<HTMLInputElement>): void => {
      const inputType = (event.nativeEvent as InputEvent).inputType;
      if (inputType !== "historyUndo" && inputType !== "historyRedo") return;
      // keydown 側で既に処理済みの操作を二重適用しないよう、ここでは既定動作を止めるだけにする。
      event.preventDefault();
    },
    []
  );

  const onPaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>): void => {
      // 既定の貼り付けは使えない（改行を空白へ畳む必要があるため）。
      event.preventDefault();
      const input = event.currentTarget;
      const pasted = event.clipboardData.getData("text/plain").replace(/\r\n|\r|\n/g, " ");
      const start = input.selectionStart ?? input.value.length;
      const end = input.selectionEnd ?? start;
      const next = `${draftRef.current.slice(0, start)}${pasted}${draftRef.current.slice(end)}`;
      pushHistory();
      const caret = start + pasted.length;
      applyDraft(next, { start: caret, end: caret }, { schedule: true });
    },
    [applyDraft, pushHistory]
  );

  const onSelect = useCallback(
    (event: React.SyntheticEvent<HTMLInputElement>): void => {
      const input = event.currentTarget;
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? start;
      rememberSelection({ start, end });
      const cell = cellRef.current;
      if (!cell) return;
      dispatcherRef.current.previewTableCellCursor(
        cell.objectId,
        { row: cell.row, column: cell.column },
        start
      );
    },
    [rememberSelection]
  );

  const onBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>): void => {
      // 表の中のスクロール・前後セルボタン・補助入力への移動は、同じ編集領域の内側なので終了しない。
      const next = event.relatedTarget;
      if (next instanceof Node && isInsideEditingSurface(next, wrapperElementRef.current)) return;
      endEdit();
    },
    [endEdit]
  );

  const wrapperRef = useCallback((element: HTMLDivElement | null): void => {
    wrapperElementRef.current = element;
  }, []);

  const inputHandlers = useMemo(
    () => ({
      onChange,
      onKeyDown,
      onBeforeInput,
      onCompositionStart,
      onCompositionEnd,
      onPaste,
      onSelect,
      onBlur,
    }),
    [
      onBeforeInput,
      onBlur,
      onChange,
      onCompositionEnd,
      onCompositionStart,
      onKeyDown,
      onPaste,
      onSelect,
    ]
  );

  // 補助入力から操作するための受け口を公開する（編集していないときは null）。
  // ハンドラはインライン入力と同じものを渡し、IME・貼り付け・セル内 Undo / Redo・送信キューの
  // 扱いが表示方式によってずれないようにする。
  useEffect(() => {
    if (!active || editingRow < 0) return undefined;
    const handle: TableCellEditorHandle = {
      objectId: object.id,
      row: editingRow,
      column: editingColumn,
      rowCount,
      columnCount,
      // 位置を決めた時点の値を渡す（現在値で組み直すと、行・列の増減後に別のセルへ当たる）。
      structureRevision: cellRef.current?.structureRevision ?? structureRevision,
      inputHandlers,
      moveCell,
      endEdit,
      prepareStyleApply,
      notifyStyleApplied,
    };
    setHandle(handle);
    // ここで選択範囲・配置基準まで消してはいけない。この effect はハンドラの同一性が変わるたびに
    // 張り直されるため、消すと編集中に選択が失われる。編集の終了時の片付けは、対象セルを切り替える
    // effect（cellRef が残っている側だけ）が行う。
    return () => setHandle(null);
  }, [
    active,
    columnCount,
    editingColumn,
    editingRow,
    endEdit,
    inputHandlers,
    moveCell,
    notifyStyleApplied,
    object.id,
    prepareStyleApply,
    rowCount,
    setHandle,
    structureRevision,
  ]);

  return {
    editingCell: active && editingRow >= 0 ? { row: editingRow, column: editingColumn } : null,
    draft,
    auxiliary,
    wrapperRef,
    inputRef: setInputElement,
    beginEditAt,
    inputHandlers,
  };
}

/**
 * セル間の移動先を求める。`appendRow` が true なら、移動の前に行を 1 行追加する必要がある。
 * 先頭セルでの Shift+Tab と最初の行での Shift+Enter は移動しない（null）。
 */
function resolveMoveTarget(
  direction: TableCellMoveDirection,
  cell: { readonly row: number; readonly column: number },
  rowCount: number,
  columnCount: number
): { readonly row: number; readonly column: number; readonly appendRow: boolean } | null {
  switch (direction) {
    case "next":
      if (cell.column + 1 < columnCount) {
        return { row: cell.row, column: cell.column + 1, appendRow: false };
      }
      if (cell.row + 1 < rowCount) return { row: cell.row + 1, column: 0, appendRow: false };
      // 最終セルでは行を 1 行追加してその先頭へ移る。
      return { row: rowCount, column: 0, appendRow: true };
    case "previous":
      if (cell.column > 0) return { row: cell.row, column: cell.column - 1, appendRow: false };
      if (cell.row > 0) return { row: cell.row - 1, column: columnCount - 1, appendRow: false };
      return null;
    case "down":
      if (cell.row + 1 < rowCount) {
        return { row: cell.row + 1, column: cell.column, appendRow: false };
      }
      return { row: rowCount, column: cell.column, appendRow: true };
    case "up":
      return cell.row > 0 ? { row: cell.row - 1, column: cell.column, appendRow: false } : null;
  }
}

/**
 * Tab / Enter による行の自動追加。上限（50 行）に達している場合は追加も移動もせず、
 * 上限へ達したことをトーストで知らせる。
 */
function appendRowForNavigation(
  table: TableCanvasObject,
  showToast: ShowToast,
  dispatcher: CanvasOpDispatcher
): boolean {
  if (table.cells.length >= TABLE_MAX_ROWS) {
    showToast("warning", `行は ${TABLE_MAX_ROWS} 行までです。これ以上は追加できません。`);
    return false;
  }
  return dispatcher.updateTableShape(table.id, {
    kind: "insert-row",
    at: table.cells.length,
  });
}

/** 編集領域（表本体・補助入力・セル書式パネル）の内側へフォーカスが移ったか。 */
function isInsideEditingSurface(next: Node, wrapper: HTMLElement | null): boolean {
  if (wrapper?.contains(next)) return true;
  if (!(next instanceof Element)) return false;
  if (next.closest("[data-table-cell-auxiliary='true']") !== null) return true;
  // 書式パネルはポータルで表の外へ描かれる。パネルの操作で編集が終わると、選択範囲ごと
  // 失われて書式を当てられなくなるため、編集領域の内側として扱う。
  return next.closest(`[${TABLE_CELL_PANEL_ATTRIBUTE}="true"]`) !== null;
}

/** セル要素の画面矩形を、書式パネルの配置基準として読む（測れない環境では null）。 */
function readCellAnchorRect(cell: HTMLElement | null): TableCellAnchorRect | null {
  if (!cell || typeof window === "undefined") return null;
  const rect = cell.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return null;
  return { x: rect.left, y: rect.top, w: rect.width, h: rect.height };
}

/**
 * インライン入力で入力・確認ができる大きさ・位置かを実測する。
 * 実測できない環境（レイアウトを持たないテスト環境など）は、切り替えの根拠にしない
 * （測れないことを理由に補助入力へ倒すと、通常の画面でもインライン入力が使えなくなる）。
 */
function canUseInlineInput(element: HTMLElement | null): boolean {
  if (!element || typeof window === "undefined") return true;
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return true;
  if (rect.width < TABLE_CELL_INLINE_MIN_WIDTH_PX) return false;
  if (rect.height < TABLE_CELL_INLINE_MIN_HEIGHT_PX) return false;
  // 画面外にある入力欄は、フォーカスが当たっても利用者から見えない。
  return (
    rect.right > 0 &&
    rect.bottom > 0 &&
    rect.left < window.innerWidth &&
    rect.top < window.innerHeight
  );
}

/**
 * 編集中セルが表のラッパーからはみ出していたら、ラッパーのスクロール位置だけを動かして寄せる。
 * `scrollIntoView` へ任せると、ページ全体や別の表までスクロールしてしまう。
 */
function scrollEditingCellIntoView(wrapper: HTMLElement | null, input: HTMLElement): void {
  if (!wrapper || typeof window === "undefined") return;
  const wrapperRect = wrapper.getBoundingClientRect();
  const inputRect = input.getBoundingClientRect();
  if (wrapperRect.width === 0 && wrapperRect.height === 0) return;
  // ステージは CSS transform で拡大縮小されるため、画面 px の差分を倍率で割ってレイアウト px へ戻す。
  const zoom = useCanvasStore.getState().view.zoom || 1;
  if (inputRect.left < wrapperRect.left) {
    wrapper.scrollLeft -= (wrapperRect.left - inputRect.left) / zoom;
  } else if (inputRect.right > wrapperRect.right) {
    wrapper.scrollLeft += (inputRect.right - wrapperRect.right) / zoom;
  }
  if (inputRect.top < wrapperRect.top) {
    wrapper.scrollTop -= (wrapperRect.top - inputRect.top) / zoom;
  } else if (inputRect.bottom > wrapperRect.bottom) {
    wrapper.scrollTop += (inputRect.bottom - wrapperRect.bottom) / zoom;
  }
}
